/**
 * Care-time log hook.
 *
 * Returns the list of care-time Observations for a patient + month,
 * including soft-deleted entries (struck-through in the drawer per
 * D9 audit requirement). Auto-refreshes on domain events for the
 * given patient.
 *
 * @agentic connections
 */

import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMedplum } from "@medplum/react";
import { searchCareTimeByPatient } from "@benchmark/core/services";
import {
  DomainEventNames,
  globalEventEmitter,
  type CareTimeCreatedEvent,
  type CareTimeDeletedEvent,
  type CareTimeUpdatedEvent,
} from "@benchmark/core/events";
import type { Observation } from "@medplum/fhirtypes";
import { getCalendarMonthBounds } from "@/utils/ui/care-time-dates";

type EventWithPatient =
  CareTimeCreatedEvent | CareTimeUpdatedEvent | CareTimeDeletedEvent;

/** Options for the care-time log hook. @agentic connections */
export type UseCareTimeLogOptions = {
  /** Month to fetch entries for; defaults to current calendar month. */
  month?: Date;
};

/** Return shape for the care-time log hook. @agentic connections */
export type UseCareTimeLogResult = {
  entries: Observation[];
  loading: boolean;
  error: Error | undefined;
  refetch: () => void;
};

/**
 * Hook: fetch care-time Observations for a patient + month. Includes
 * soft-deleted (entered-in-error) entries so the drawer can render
 * them struck-through per D9.
 * @agentic connections
 */
export const useCareTimeLog = (
  patientId: string | undefined,
  options: UseCareTimeLogOptions = {},
): UseCareTimeLogResult => {
  const medplum = useMedplum();
  const [entries, setEntries] = useState<Observation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [version, setVersion] = useState(0);
  const monthKey =
    options.month instanceof Date
      ? options.month.toISOString().slice(0, 7)
      : new Date().toISOString().slice(0, 7);

  const monthBounds = useMemo(
    () => getCalendarMonthBounds(options.month ?? new Date()),
    [monthKey],
  );

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!patientId) {
      return;
    }
    let cancelled = false;

    startTransition(() => {
      setLoading(true);
      setError(undefined);
    });

    searchCareTimeByPatient(medplum, patientId, {
      startDate: monthBounds.start,
      endDate: monthBounds.end,
      count: 200,
      includeSoftDeleted: true,
    })
      .then((result) => {
        if (cancelled) return;
        setEntries(result);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [medplum, patientId, monthBounds.start, monthBounds.end, version]);

  // Keep the latest refetch closure on a ref so the subscription
  // doesn't have to re-bind on every render. Stable subscription
  // identity matters under React 18 StrictMode double-mount.
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  });

  useEffect(() => {
    if (!patientId) return undefined;
    const onEvent = (payload: EventWithPatient) => {
      if (payload.patientId === patientId) {
        refetchRef.current();
      }
    };
    const unsubCreated = globalEventEmitter.subscribe<CareTimeCreatedEvent>(
      DomainEventNames.CareTimeCreated,
      onEvent,
    );
    const unsubUpdated = globalEventEmitter.subscribe<CareTimeUpdatedEvent>(
      DomainEventNames.CareTimeUpdated,
      onEvent,
    );
    const unsubDeleted = globalEventEmitter.subscribe<CareTimeDeletedEvent>(
      DomainEventNames.CareTimeDeleted,
      onEvent,
    );
    return () => {
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
    };
  }, [patientId]);

  return {
    entries: patientId ? entries : [],
    loading: patientId ? loading : false,
    error: patientId ? error : undefined,
    refetch,
  };
};
